import React from 'react'
import { useMutation, useQuery, UseQueryOptions } from 'react-query'
import {
  compose,
  filter,
  forEach,
  groupBy,
  includes,
  isNil,
  keys,
  map,
  mapObjIndexed,
  path,
  pathOr,
  prop,
  sum,
  values,
} from 'ramda'
import useComapnyCode from '@/hooks/useComapnyCode'
import { useAcTypeList, useDepartmentFullList, useFormTypeList } from '@/options'
import request, { convertData, Mutation, MutationOptions, useMakeMutation } from '@/services'
import genUnitKey from '@/utils/genUnitKey'
import { expoNumber, toEquivalentEmissionFormat } from '@/utils/numbers'
import { ActTypeEnum } from './act.enum'
import {
  IAct1Data,
  IAct2Data,
  IActHistory,
  IActReview,
  IPreCalculateBody,
  IActOtherEnergy,
  IEstimateQuantity,
  IEstimateQuantityQuery,
  IActReviewSetting,
  IActReviewLog,
  IMatchCoefficients,
  IMatchCoefficientResult,
} from './act.type'
import { IAct4Data } from './actOtherEnergy.type'

export const useActMatchCoefficients = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type1/matchCoefficients', {
        method: 'POST',
        body: { ...body, companyCode },
        displayMessage: false,
      }),
    options
  )
}
/**
 * 係數選用
 * 修改畫面顯示公告係數和地區係數, 依時間順序排序
 */
export const useActMatchCoefficientsV2 = ({
  onSuccess,
  payload,
  ...options
}: MutationOptions & { payload?: any }) => {
  const companyCode = useComapnyCode()
  const [responseData, setResponseData] = React.useState<any>(null)

  const handleSuccess = React.useCallback(
    (data: IMatchCoefficients & { calorificValueType: number; coefficientUseType: number }) => {
      // detail的狀態
      let calorificValueType = data?.calorificValueType
      let coefficientUseType = data?.coefficientUseType

      if (data.results.length > 0) {
        const matchCoefficientResult = pathOr({}, ['results', 0], data) as any
        // 等於 -1 時, 為[自訂] 不等於時,保持原本detail的狀態
        if (matchCoefficientResult?.defaultCalorificValue === -1) {
          calorificValueType = 1
        }

        if (matchCoefficientResult?.coefficientSource === '-1') {
          coefficientUseType = 1
        }
      }

      onSuccess && onSuccess({ ...data, calorificValueType, coefficientUseType })
    },
    []
  )

  const { mutate, isLoading, data, ...rest } = useMutation(
    (body: any) =>
      request('/{companyCode}/act/type1/matchCoefficients', {
        method: 'POST',
        body: { ...body, companyCode },
        displayMessage: false,
      }),
    {
      onSuccess: res => setResponseData(res),
      ...options,
    }
  )

  React.useEffect(() => {
    if (responseData && payload) {
      const data = path(['data'], responseData) as IMatchCoefficients

      handleSuccess({ ...data, ...payload })
    }
  }, [responseData, payload])

  //預設的來源清單
  const coefficientSources = React.useMemo(() => {
    let sources: string[] = []

    compose(
      forEach((i: IMatchCoefficientResult) => {
        if (!includes(i.coefficientSource, sources)) {
          sources.push(i.coefficientSource)
        }
        return { ...i }
      }),
      filter((i: IMatchCoefficientResult) => i.coefficientSource !== '-1')
    )(pathOr([], ['data', 'results'], data))

    return sources
  }, [data])

  return {
    ...rest,
    mutate,
    coefficientSources,
    matchCoefficientResult: pathOr(
      null,
      ['data', 'results', 0],
      data
    ) as IMatchCoefficientResult | null,
  }
}

export const usePreCalculate = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: IPreCalculateBody) =>
      request('/{companyCode}/act/type1/preCalculate', {
        method: 'POST',
        body: { ...body, companyCode },
        displayMessage: false,
      }),
    options
  )
}

export const useAddActType1 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type1/activity', {
        method: 'POST',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useEditActType1 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type1/activity', {
        method: 'PUT',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useDeleteActType1 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type1/activity', {
        method: 'DELETE',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useActTypeList = (query?: any) => {
  const companyCode = useComapnyCode()
  const { byId } = useAcTypeList()
  const { showDepartmentWithOrganization } = useDepartmentFullList()
  const { byId: formTypeById } = useFormTypeList()

  const { data, isLoading, refetch } = useQuery(
    ['/{companyCode}/act/type1/activities', { companyCode, ...query }],
    { refetchOnMount: true }
  )

  const dataSource = React.useMemo(
    () =>
      convertData<IAct1Data>(
        {
          field: ['data'],
          converter: x => ({
            ...x,
            actTypeName: byId[x.actType]?.desc || x.actType,
            formTypeName: formTypeById[x.formType]?.name,
            sessionName: showDepartmentWithOrganization(x.sessionSeq),
            totalEmission: toEquivalentEmissionFormat(x.totalEmission),
          }),
        },
        data
      ),
    [data, byId, showDepartmentWithOrganization, formTypeById]
  )

  return { dataSource, isLoading, refetch }
}

export const useActTypeById = (variables: {
  id?: number
  actType?: number
  isLeased?: boolean
}) => {
  const companyCode = useComapnyCode()

  return useQuery<IAct1Data>(
    ['/{companyCode}/act/type1/activity', { ...variables, companyCode, actSeq: variables.id }],
    {
      enabled: !!variables.id && !!companyCode,
      select: (res: any) => prop('data', res),
      refetchOnMount: true,
    }
  )
}

export const useActType2List = (query?: any) => {
  const companyCode = useComapnyCode()
  const { byId } = useAcTypeList()
  const { showDepartmentWithOrganization } = useDepartmentFullList()
  const { byId: formTypeById } = useFormTypeList()

  const { data, isLoading, refetch } = useQuery([
    '/{companyCode}/act/type2/activities',
    { companyCode, ...query },
  ])

  const dataSource = React.useMemo(
    () =>
      convertData<IAct2Data>(
        {
          field: ['data'],
          converter: x => ({
            ...x,
            actTypeName: byId[x.actType]?.desc || x.actType,
            formTypeName: formTypeById[x.formType]?.name,
            sessionName: showDepartmentWithOrganization(x.sessionSeq),
            totalEmission: toEquivalentEmissionFormat(x.totalEmission),
          }),
        },
        data
      ),
    [data, byId, showDepartmentWithOrganization, formTypeById]
  )

  return { dataSource, isLoading, refetch }
}

export const useAddActType2 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type2/activity', {
        method: 'POST',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useEditActType2 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type2/activity', {
        method: 'PUT',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useDeleteActType2 = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/type2/activity', {
        method: 'DELETE',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useActType2ById = (variables: {
  id?: number
  actType: number
  isLeased?: boolean
}) => {
  const companyCode = useComapnyCode()

  return useQuery<IAct2Data>(
    ['/{companyCode}/act/type2/activity', { ...variables, companyCode, actSeq: variables.id }],
    {
      enabled: !!variables.id && !!companyCode,
      select: (res: any) => prop('data', res),
      refetchOnMount: true,
    }
  )
}

export const useAcReviewList = (query?: any) => {
  const companyCode = useComapnyCode({ supportSystemAdminCode: true })
  const { byId } = useAcTypeList()
  const { byId: formTypeById } = useFormTypeList()

  const { data, isFetching, refetch } = useQuery(
    ['/{companyCode}/act/review/activities', { companyCode, ...query }],
    {
      refetchOnMount: true,
    }
  )

  const dataSource = React.useMemo(
    () =>
      convertData<IActReview>(
        {
          field: ['data'],
          converter: x => ({
            ...x,
            actTypeName: byId[x?.actType]?.desc || x.actType,
            totalEmission: toEquivalentEmissionFormat(x.totalEmission),
            totalEmissionNum: x.totalEmission,
            formTypeName: formTypeById[x.formType]?.name,
            inputPowerDetails: x.inputPowerDetails.map(i => {
              const unitOpt = i?.fuelDto?.unitOptions?.find(
                x => x.unitSeq === i.originUnitSeq && x.unitType === i.originUnitType
              )
              return { ...i, unitName: unitOpt?.unitName }
            }),
            groupScrapDetails: compose(
              values,
              mapObjIndexed((value: any, key, obj) => {
                const emission = value.map((i: any) => i.emission)
                const quantity = value.map((i: any) => i.quantity)

                const emissionFormat = toEquivalentEmissionFormat(sum(emission))
                const quantityFormat = expoNumber(sum(quantity))
                const totalQuantity = sum(quantity)
                // 若有多個自訂單位時無法加總，故顯示 '-'
                const unitCount = keys(groupBy((i: any) => i.originUnitSeq)(value)).length
                if (unitCount > 1) {
                  return {
                    mainCategory: key,
                    emission: emissionFormat,
                    quantity: undefined,
                    quantityStr: '-',
                    scrapUnit: '',
                  }
                }

                return {
                  mainCategory: key,
                  emission: emissionFormat,
                  quantity: totalQuantity,
                  quantityStr: quantityFormat,
                  scrapUnit: value[0]?.selfDefinitionObj?.scrapUnit,
                }
              }),
              groupBy((i: any) => i.selfDefinitionObj?.mainCategory),
              filter(
                (scrapDetail: any) =>
                  !!scrapDetail?.selfDefinitionObj?.mainCategory && !isNil(scrapDetail?.quantity)
              )
            )(x.scrapDetails || x.scrapWaterDetails || []),
          }),
        },
        data
      ),
    [data, byId, formTypeById]
  )

  return { dataSource, isLoading: isFetching, refetch }
}

export const useEditActReview = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/review/activity/action', {
        method: 'POST',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useBatchEditActReview = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode({ supportSystemAdminCode: true })

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/review/activity/action/batch', {
        method: 'POST',
        body: { ...body, companyCode },
      }),
    options
  )
}

export const useActHistoryList = (query?: any) => {
  const companyCode = useComapnyCode()
  const { byId } = useAcTypeList()
  const { showDepartmentWithOrganization } = useDepartmentFullList()

  const { data, isLoading, refetch } = useQuery([
    '/{companyCode}/act/history/activitys',
    { companyCode, ...query },
  ])

  const dataSource = React.useMemo(
    () =>
      convertData<IActHistory>(
        {
          field: ['data'],
          converter: x => ({
            ...x,
            actTypeName: byId[x.actType]?.desc || x.actType,
            sessionName: showDepartmentWithOrganization(x.sessionSeq),
            totalEmission: toEquivalentEmissionFormat(x.totalEmission),
          }),
        },
        data
      ),
    [data, byId, showDepartmentWithOrganization]
  )

  return { dataSource, isLoading, refetch }
}

export const useActOtherEnergy = (query?: any) => {
  const companyCode = useComapnyCode()
  const { byId } = useAcTypeList()
  const { showDepartmentWithOrganization } = useDepartmentFullList()

  const { data, isLoading, refetch } = useQuery(
    ['/{companyCode}/act/type4/activities/actOtherEnergy', { companyCode, ...query }],
    { enabled: !!companyCode, refetchOnMount: true }
  )

  const dataSource = React.useMemo(
    () =>
      convertData<IActOtherEnergy>(
        {
          field: ['data'],
          converter: x => ({
            ...x,
            actTypeName: byId[x?.actType]?.desc || x?.actType,
            sessionName: showDepartmentWithOrganization(x.sessionSeq),
            totalEmission: toEquivalentEmissionFormat(x.totalEmission),
          }),
        },
        data
      ),
    [data, byId, showDepartmentWithOrganization]
  )

  return { dataSource, isLoading, refetch }
}

export const useImportMovableCombustionByCPC = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request(`/${companyCode}/act/type1/activity/importCpc`, {
        method: 'POST',
        body,
        headers: {},
      }),
    options
  )
}

export const useImportMovableCombustionByCPC_FLEET = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: any) =>
      request(`/${companyCode}/act/type1/activity/importCpcFleet`, {
        method: 'POST',
        body,
        headers: {},
      }),
    options
  )
}

export const usePowerInputEstimateQuantity = (options?: UseQueryOptions<IEstimateQuantity>) => {
  const companyCode = useComapnyCode()

  const [queryCache, setQueryCache] = React.useState(1)
  const [query, setQuery] = React.useState<IEstimateQuantityQuery | null>(null)

  const { refetch, ...others } = useQuery<IEstimateQuantity>(
    [
      '/{companyCode}/act/type2/activity/inputPower/estimateQuantity',
      { ...query, companyCode, queryCache },
    ],
    {
      enabled: !!query && !!companyCode,
      select: (res: any) => prop('data', res),
      refetchOnMount: false,
      ...options,
    }
  )

  const fetch = React.useCallback((query: IEstimateQuantityQuery) => {
    setQuery(query)
    setQueryCache(c => ++c)
  }, [])

  return { fetch, ...others }
}

export const useWaterUsageEstimateQuantity = (options?: UseQueryOptions<IEstimateQuantity>) => {
  const companyCode = useComapnyCode()

  const [queryCache, setQueryCache] = React.useState(1)
  const [query, setQuery] = React.useState<IEstimateQuantityQuery | null>(null)

  const { refetch, ...others } = useQuery<IEstimateQuantity>(
    [
      '/{companyCode}/act/type4/activity/actWaterUsage/estimateQuantity',
      { ...query, companyCode, queryCache },
    ],
    {
      enabled: !!query && !!companyCode,
      select: (res: any) => prop('data', res),
      refetchOnMount: false,
      ...options,
    }
  )

  const fetch = React.useCallback((query: IEstimateQuantityQuery) => {
    setQuery(query)
    setQueryCache(c => ++c)
  }, [])

  return { fetch, ...others }
}

export const useActSimpleFillin = (
  variables: { actSimpleFillin?: number },
  options?: UseQueryOptions<IAct1Data>
) => {
  const companyCode = useComapnyCode()

  return useQuery<IAct1Data>(['/{companyCode}/act/simpleFillin', { ...variables, companyCode }], {
    enabled: !!variables.actSimpleFillin,
    select: (res: any) => prop('data', res),
    refetchOnMount: true,
    ...options,
  })
}

export const useActReviewSetting = () => {
  const companyCode = useComapnyCode({ supportSystemAdminCode: true })

  return useQuery<IActReviewSetting[]>(['/{companyCode}/act/review/setting', { companyCode }], {
    select: (res: any) => prop('data', res),
    refetchOnMount: true,
  })
}

export const useUpdateActReviewSetting = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode({ supportSystemAdminCode: true })

  return useMakeMutation(
    (body: any) =>
      request('/{companyCode}/act/review/setting', {
        method: 'PUT',
        body: { ...body, companyCode },
      }),
    options
  )
}
export const useActReviewLog = (
  variables: {
    actType?: number
    actSeq?: number
    companyCode?: string
  },
  options?: UseQueryOptions<IActReviewLog[]>
) => {
  const companyCode = useComapnyCode()

  return useQuery<IActReviewLog[]>(
    [
      '/{companyCode}/act/review/activity/history',
      {
        ...variables,
        companyCode: companyCode || variables?.companyCode,
      },
    ],
    {
      enabled: !!variables.actSeq && !!variables.actType,
      select: (res: any) => prop('data', res),
      refetchOnMount: true,
      ...options,
    }
  )
}

/**
 * 其他能源[詳細資料]
 */
export const useActOtherEnergyById = (variables: { id?: number }) => {
  const companyCode = useComapnyCode()

  const { data, ...others } = useQuery<IAct4Data>(
    [
      '/{companyCode}/act/type4/activity/actOtherEnergy',
      { ...variables, companyCode, actSeq: variables.id },
    ],
    {
      enabled: !!variables.id && !!companyCode,
      select: (res: any) => prop('data', res),
      refetchOnMount: true,
    }
  )

  const convertData: any = React.useMemo(() => {
    if (!data) {
      return
    }

    const { actType2InputPowerDto, details, actType1BaseDto, ...othersData } = data

    const isActType2 = data.sourceActType === ActTypeEnum.PowerInput

    const { actType, details: type2DtoDetails, id, ...type2Dto } = actType2InputPowerDto || {}

    const type4Details = details?.map((i: any) => {
      const de = type2DtoDetails?.find((x: any) => x.fuel === i.sourceFuel)
      const { fuel, fuelDto, ...others } = de || {}
      return { fuel: i.fuel, fuelDto: i.fuelDto, ...others }
    })

    const type4DetailsWithUnit = compose(
      map((i: any) => ({
        ...i,
        unitKey: genUnitKey(i.originUnitType, i.originUnitSeq),
      }))
    )(type4Details || [])

    //TODO isActType1
    const { ...type1Dto } = actType1BaseDto || {}

    const newData = {
      ...(isActType2
        ? {
            ...type2Dto,
            nonRenewEnergyDetailDto: type4DetailsWithUnit.filter(x => x.nonRenewable),
            renewEnergyDetailDto: type4DetailsWithUnit.filter(x => !x.nonRenewable),
          }
        : { ...type1Dto }),
      ...othersData,
      actType1BaseDto,
      actType2InputPowerDto,
    }

    return newData
  }, [data])

  return { data: convertData, ...others }
}

/**
 * convertIPreCalculateActOtherEnergyResp
 */
export const usePreCalculateActOtherEnergy = (options: MutationOptions = {}): Mutation => {
  const companyCode = useComapnyCode()

  return useMakeMutation(
    (body: IPreCalculateBody) =>
      request('/{companyCode}/act/type4/preCalculate/actOtherEnergy', {
        method: 'POST',
        body: { ...body, companyCode },
        displayMessage: false,
      }),
    options
  )
}
